
import licenseApi from "../api/license/licenseApi";
import { defineComponent, onMounted, ref, watch, computed } from "vue";
import CrmNavbar from "./Navbar.vue";
import { useOfficeState } from "../store/officeState";
import { licenseLevel } from "../model/LicenseLevel";
import { debounce } from "lodash";
import leadApi from "../api/lead/leadsApi";
import LeadPreview from "../model/LeadPreview";
import CrmLeadPreview from "../components/Lead/LeadPreview.vue";
import { useEmailService } from "../services/emailService";
import emailMessagesApi from "../api/emailMessage/emailMessagesApi";
import usersApi from "../api/user/users";
import { useRouter } from "../router";
import { distinctById, excludeEmailAddresses, getMailboxItemEmailAddresses } from "../helpers/emailAddressHelper";

/* global Office */

export default defineComponent({
  name: "CrmLeadEmail",
  components: { CrmNavbar, CrmLeadPreview },
  setup() {
    const searchText = ref("" as string);
    const isLoading = ref(false as boolean);
    const { mailboxItem } = useOfficeState();
    const isLicenseLevelValid = ref(true);
    const leadList = ref([] as LeadPreview[]);
    const { associateEmailToLead } = useEmailService();
    const associatedLeads = ref([] as LeadPreview[]);
    const senderEmail = ref("" as string);
    const emailAddresses = ref([] as Office.EmailAddressDetails[]);
    const router = useRouter();
    let lastSearchId = 0;

    const associatedLeadsIds = computed(() => {
      return associatedLeads.value.map((ab) => ab.id);
    });

    // Cannot use directly mailboxItem because it is a computedRef (not compatible with Office js lib)
    let currentMailBoxItem = Office.context.mailbox.item;
    let from = ref(currentMailBoxItem?.from as Office.EmailAddressDetails);

    /**
     * Watch Outlook mailbox item changed
     */
    watch(mailboxItem, async () => {
      currentMailBoxItem = Office.context.mailbox.item;
      from.value = currentMailBoxItem?.from as Office.EmailAddressDetails;
      debounceSearch.cancel();
      await setAssociatedLeads();
      await refreshLeadList();
    });

    /**
     * When component is mounted
     */
    onMounted(async () => {
      await initLicenceLevelValidity();
      await setAssociatedLeads();
      await searchLeadsByEmail();
    });

    /**
     * Initialize licence level validity
     */
    async function initLicenceLevelValidity() {
      let result = await licenseApi.getMobiCrmLicense();
      if (result.isSuccess) {
        isLicenseLevelValid.value = result.data.level >= licenseLevel.PRO;
      }
    }

    /**
     * Navigates to lead url
     * @param leadUrl
     */
    function navigateToLeadUrl(leadUrl: string) {
      if (!leadUrl) {
        return;
      }
      if (Office.context.ui.openBrowserWindow) {
        Office.context.ui.openBrowserWindow(leadUrl);
      } else {
        window.open(leadUrl);
      }
    }

    /**
     * Runs the search on the server
     **/
    const debounceSearch = debounce(refreshLeadList, 500);

    async function refreshLeadList() {
      if (searchText.value) {
        await searchLeadsByText();
      } else {
        await searchLeadsByEmail();
      }
    }

    async function searchLeadsByText() {
      const searchId = ++lastSearchId;
      isLoading.value = true;
      try {
        const result = await leadApi.quicksearch(searchText.value);
        if (searchId === lastSearchId && result.isSuccess) {
          leadList.value = result.data.items.filter((b: LeadPreview) => !associatedLeadsIds.value.includes(b.id));
        }
      } finally {
        if (searchId === lastSearchId) {
          isLoading.value = false;
        }
      }
    }

    /**
     * Associates current email to selected lead
     * @param leadId
     */
    async function associateCurrentEmailToLead(leadId: string) {
      isLoading.value = true;
      await associateEmailToLead(leadId, Office.context.mailbox.item, associateCurrentEmailToLeadCallback);
    }

    /**
     * Associates current email to selected contact callback
     */
    async function associateCurrentEmailToLeadCallback() {
      await setAssociatedLeads();
      isLoading.value = false;
    }

    /**
     * Sets associated leads ids
     */
    async function setAssociatedLeads() {
      isLoading.value = true;
      if (Office.context.mailbox.item) {
        let result = await emailMessagesApi.getAssociatedLeads(Office.context.mailbox.item.itemId);
        if (result.isSuccess) {
          associatedLeads.value = result.data;
        } else {
          associatedLeads.value = [];
        }
      }
      isLoading.value = false;
    }

    /**
     * Creates new lead
     */
    async function createNewLead() {
      await navigateToLeadEntityCreation(from.value);
    }

    /**
     * Search leads by email
     */
    async function searchLeadsByEmail() {
      const searchId = ++lastSearchId;
      leadList.value = [];
      emailAddresses.value = [];
      if (!currentMailBoxItem?.from) {
        return;
      }

      isLoading.value = true;
      try {
        senderEmail.value = currentMailBoxItem.from.emailAddress;
        const itemEmailAddresses = getMailboxItemEmailAddresses(currentMailBoxItem);

        const leadsByEmailResult = await leadApi.getLeadsByEmails(itemEmailAddresses.map((t) => t.emailAddress));
        const usersResult = await usersApi.getUsers();
        if (searchId !== lastSearchId) {
          return;
        }

        const foundLeads: LeadPreview[] = leadsByEmailResult.isSuccess ? leadsByEmailResult.data : [];
        leadList.value = distinctById(foundLeads)
          .filter((b) => !associatedLeadsIds.value.includes(b.id))
          .sort((a, b) => (a.LastName ?? "").localeCompare(b.LastName ?? ""));

        const usersEmailAddresses = usersResult.isSuccess ? usersResult.data.map((u: any) => u.email) : [];
        emailAddresses.value = excludeEmailAddresses(itemEmailAddresses, [
          ...foundLeads.map((l) => l.contactEmailAddress),
          ...usersEmailAddresses,
        ]);
      } finally {
        if (searchId === lastSearchId) {
          isLoading.value = false;
        }
      }
    }

    /**
     * Navigates to lead entity creation
     */
    async function navigateToLeadEntityCreation(emailAddressDetails: Office.EmailAddressDetails) {
      await router.push({
        name: "lead-entity-creation",
        params: {
          emailAddress: emailAddressDetails.emailAddress,
          name: emailAddressDetails.displayName,
          isSender: emailAddressDetails.emailAddress === senderEmail.value,
        },
      });
    }

    return {
      from,
      searchText,
      navigateToLeadUrl,
      isLoading,
      isLicenseLevelValid,
      debounceSearch,
      leadList,
      associatedLeads,
      associateCurrentEmailToLead,
      associatedLeadsIds,
      createNewLead,
      emailAddresses,
      navigateToLeadEntityCreation,
    };
  },
});
